// Admin Panel - Settings Page
// File: admin-panel/src/pages/Settings.jsx

import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../services/firebase'
import {
  Cog6ToothIcon,
  BellIcon,
  BanknotesIcon,
  ClockIcon,
  MapPinIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

function Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [settings, setSettings] = useState({
    // Delivery Settings
    defaultDeliveryTime: 45,
    graceTimePeriod: 10,
    maxDeliveriesPerDay: 20,
    autoAssignTasks: false,
    
    // Cash Deposit Settings
    maxCashInHand: 10000,
    depositReminderDays: 2,
    requireProofForDeposit: true,
    
    // Notification Settings
    enablePushNotifications: true,
    notifyOnNewTask: true,
    notifyOnDeposit: true,
    notifyOnDelayedDelivery: true,
    
    // Business Settings
    companyName: 'Porter Delivery',
    businessAddress: '',
    gstNumber: '',
    supportPhone: '',
    supportEmail: '',
    
    // Working Hours
    workingHoursStart: '09:00',
    workingHoursEnd: '21:00',
    workingDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'],
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'settings', 'app'));
        if (settingsDoc.exists()) {
          setSettings(prev => ({ ...prev, ...settingsDoc.data() }));
        }
      } catch (error) {
        console.error('Fetch settings error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'settings', 'app'), {
        ...settings,
        updatedAt: new Date().toISOString(),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Save settings error:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleWorkingDay = (day) => {
    setSettings(prev => ({
      ...prev,
      workingDays: prev.workingDays.includes(day)
        ? prev.workingDays.filter(d => d !== day)
        : [...prev.workingDays, day]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Settings</h1>
          <p className="text-gray-400 mt-1">Configure your delivery management system</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className={`flex items-center gap-2 px-6 py-2 font-medium rounded-xl transition-all ${
            saved 
              ? 'bg-green-600 text-white' 
              : 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white shadow-lg shadow-red-500/20'
          } disabled:opacity-50`}
        >
          {saved ? (
            <>
              <CheckCircleIcon className="w-5 h-5" />
              Saved!
            </>
          ) : saving ? (
            'Saving...'
          ) : (
            'Save Changes'
          )}
        </button>
      </div>

      {/* Delivery Settings */}
      <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
            <ClockIcon className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Delivery Settings</h2>
            <p className="text-gray-400 text-sm">Configure delivery time and limits</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-gray-400 text-sm font-medium mb-2">Default Delivery Time (minutes)</label>
            <input
              type="number"
              value={settings.defaultDeliveryTime}
              onChange={(e) => setSettings({ ...settings, defaultDeliveryTime: parseInt(e.target.value) })}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm font-medium mb-2">Grace Period (minutes)</label>
            <input
              type="number"
              value={settings.graceTimePeriod}
              onChange={(e) => setSettings({ ...settings, graceTimePeriod: parseInt(e.target.value) })}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm font-medium mb-2">Max Deliveries Per Day</label>
            <input
              type="number"
              value={settings.maxDeliveriesPerDay}
              onChange={(e) => setSettings({ ...settings, maxDeliveriesPerDay: parseInt(e.target.value) })}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="autoAssign"
              checked={settings.autoAssignTasks}
              onChange={(e) => setSettings({ ...settings, autoAssignTasks: e.target.checked })}
              className="w-5 h-5 rounded bg-gray-700 border-gray-600 text-red-500 focus:ring-red-500"
            />
            <label htmlFor="autoAssign" className="text-white font-medium">Auto-assign tasks to available porters</label>
          </div>
        </div>
      </div>

      {/* Cash Deposit Settings */}
      <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center">
            <BanknotesIcon className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Cash Deposit Settings</h2>
            <p className="text-gray-400 text-sm">Configure cash handling rules</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-gray-400 text-sm font-medium mb-2">Max Cash In Hand (₹)</label>
            <input
              type="number"
              value={settings.maxCashInHand}
              onChange={(e) => setSettings({ ...settings, maxCashInHand: parseInt(e.target.value) })}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm font-medium mb-2">Deposit Reminder (days)</label>
            <input
              type="number"
              value={settings.depositReminderDays}
              onChange={(e) => setSettings({ ...settings, depositReminderDays: parseInt(e.target.value) })}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="requireProof"
              checked={settings.requireProofForDeposit}
              onChange={(e) => setSettings({ ...settings, requireProofForDeposit: e.target.checked })}
              className="w-5 h-5 rounded bg-gray-700 border-gray-600 text-red-500 focus:ring-red-500"
            />
            <label htmlFor="requireProof" className="text-white font-medium">Require proof image for deposits</label>
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-yellow-500/20 rounded-xl flex items-center justify-center">
            <BellIcon className="w-5 h-5 text-yellow-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Notification Settings</h2>
            <p className="text-gray-400 text-sm">Configure push notifications</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-gray-700">
            <div>
              <p className="text-white font-medium">Enable Push Notifications</p>
              <p className="text-gray-400 text-sm">Send notifications to porter app</p>
            </div>
            <input
              type="checkbox"
              checked={settings.enablePushNotifications}
              onChange={(e) => setSettings({ ...settings, enablePushNotifications: e.target.checked })}
              className="w-5 h-5 rounded bg-gray-700 border-gray-600 text-red-500 focus:ring-red-500"
            />
          </div>
          <div className="flex items-center justify-between py-3 border-b border-gray-700">
            <div>
              <p className="text-white font-medium">New Task Notification</p>
              <p className="text-gray-400 text-sm">Notify when a new task is assigned</p>
            </div>
            <input
              type="checkbox"
              checked={settings.notifyOnNewTask}
              onChange={(e) => setSettings({ ...settings, notifyOnNewTask: e.target.checked })}
              className="w-5 h-5 rounded bg-gray-700 border-gray-600 text-red-500 focus:ring-red-500"
            />
          </div>
          <div className="flex items-center justify-between py-3 border-b border-gray-700">
            <div>
              <p className="text-white font-medium">Deposit Reminder</p>
              <p className="text-gray-400 text-sm">Remind porters to deposit cash</p>
            </div>
            <input
              type="checkbox"
              checked={settings.notifyOnDeposit}
              onChange={(e) => setSettings({ ...settings, notifyOnDeposit: e.target.checked })}
              className="w-5 h-5 rounded bg-gray-700 border-gray-600 text-red-500 focus:ring-red-500"
            />
          </div>
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="text-white font-medium">Delayed Delivery Alert</p>
              <p className="text-gray-400 text-sm">Alert when delivery exceeds time limit</p>
            </div>
            <input
              type="checkbox"
              checked={settings.notifyOnDelayedDelivery}
              onChange={(e) => setSettings({ ...settings, notifyOnDelayedDelivery: e.target.checked })}
              className="w-5 h-5 rounded bg-gray-700 border-gray-600 text-red-500 focus:ring-red-500"
            />
          </div>
        </div>
      </div>

      {/* Business Settings */}
      <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center">
            <ShieldCheckIcon className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Business Information</h2>
            <p className="text-gray-400 text-sm">Your company details</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-gray-400 text-sm font-medium mb-2">Company Name</label>
            <input
              type="text"
              value={settings.companyName}
              onChange={(e) => setSettings({ ...settings, companyName: e.target.value })}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm font-medium mb-2">GST Number</label>
            <input
              type="text"
              value={settings.gstNumber}
              onChange={(e) => setSettings({ ...settings, gstNumber: e.target.value })}
              placeholder="e.g., 22AAAAA0000A1Z5"
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-gray-400 text-sm font-medium mb-2">Business Address</label>
            <textarea
              value={settings.businessAddress}
              onChange={(e) => setSettings({ ...settings, businessAddress: e.target.value })}
              rows={2}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm font-medium mb-2">Support Phone</label>
            <input
              type="tel"
              value={settings.supportPhone}
              onChange={(e) => setSettings({ ...settings, supportPhone: e.target.value })}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm font-medium mb-2">Support Email</label>
            <input
              type="email"
              value={settings.supportEmail}
              onChange={(e) => setSettings({ ...settings, supportEmail: e.target.value })}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
        </div>
      </div>

      {/* Working Hours */}
      <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-red-500/20 rounded-xl flex items-center justify-center">
            <MapPinIcon className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Working Hours</h2>
            <p className="text-gray-400 text-sm">Set operating hours and days</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-gray-400 text-sm font-medium mb-2">Start Time</label>
            <input
              type="time"
              value={settings.workingHoursStart}
              onChange={(e) => setSettings({ ...settings, workingHoursStart: e.target.value })}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm font-medium mb-2">End Time</label>
            <input
              type="time"
              value={settings.workingHoursEnd}
              onChange={(e) => setSettings({ ...settings, workingHoursEnd: e.target.value })}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-gray-400 text-sm font-medium mb-3">Working Days</label>
          <div className="flex flex-wrap gap-2">
            {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
              <button
                key={day}
                type="button"
                onClick={() => toggleWorkingDay(day)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${
                  settings.workingDays.includes(day)
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                }`}
              >
                {day.slice(0, 3)}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;